import { useMutation, useQuery, useQueryClient } from 'react-query';
import { httpRequest } from '../config/api';
import {
  GET_SHEET_CATEGORIES_FOR_PERIOD,
  GET_SHEET_CATEGORIES_FOR_SHEET
} from '../consts/queryKeys';

/**
 * Sheet ↔ data category mapping service.
 *
 * The backend OpenAPI spec exposes these schemas as
 * `SheetCategoryResponseSchema` / `SheetCategoryCreateRequestSchema` /
 * `SheetCategoryUpdateRequestSchema`. They are not yet in the local
 * generated types (the schema URL was empty when this file was added);
 * re-run `yarn generate-types` and switch consumers to the re-exports in
 * `src/types/workflow.ts` once it's wired.
 *
 * Mapping shape (server response):
 *   { id, sheet_id, period_id, workflow_id, data_category,
 *     is_custom, custom_category_id }
 *
 * `X-Active-Company-Id` and `workflow_id` headers are auto-injected by
 * `src/config/api.js`, so callers never set them explicitly.
 */
export const sheetCategoriesService = {
  getForPeriod: periodId => httpRequest.get(`api/periods/${periodId}/sheet-categories`),

  getForSheet: sheetId => httpRequest.get(`api/sheets/${sheetId}/sheet-categories`),

  create: data => httpRequest.post('api/sheet-categories', data),

  update: ({ id, data }) => httpRequest.put(`api/sheet-categories/${id}`, data),

  deleteOne: id => httpRequest.delete(`api/sheet-categories/${id}`),

  deleteAllForSheet: sheetId => httpRequest.delete(`api/sheets/${sheetId}/sheet-categories`),

  copyFromPreviousPeriod: data => httpRequest.post('api/automation/sheet-category-copy', data),

  mapWithAI: (periodId, sheetIds) =>
    httpRequest.post(
      `api/periods/${periodId}/sheet-categories/ai-map`,
      sheetIds?.length ? { sheet_ids: sheetIds } : undefined
    )
};

const invalidateAll = (queryClient, periodId, sheetId) => {
  if (periodId) {
    queryClient.invalidateQueries([GET_SHEET_CATEGORIES_FOR_PERIOD, periodId]);
    // `category_mapping_progress_status` feeds the stepper's Mapping label — otherwise stale.
    queryClient.invalidateQueries(['GET_PERIOD_BY_ID']);
  }
  if (sheetId) {
    queryClient.invalidateQueries([GET_SHEET_CATEGORIES_FOR_SHEET, sheetId]);
  }
};

export const useGetSheetCategoriesForPeriod = ({ periodId, queryParams = {} } = {}) => {
  return useQuery(
    [GET_SHEET_CATEGORIES_FOR_PERIOD, periodId],
    () => sheetCategoriesService.getForPeriod(periodId),
    {
      enabled: !!periodId,
      ...queryParams
    }
  );
};

export const useGetSheetCategoriesForSheet = ({ sheetId, queryParams = {} } = {}) => {
  return useQuery(
    [GET_SHEET_CATEGORIES_FOR_SHEET, sheetId],
    () => sheetCategoriesService.getForSheet(sheetId),
    {
      enabled: !!sheetId,
      ...queryParams
    }
  );
};

export const useCreateSheetCategory = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.create, {
    onSuccess: (_data, variables) => invalidateAll(queryClient, periodId, variables?.sheet_id),
    ...queryParams
  });
};

export const useUpdateSheetCategory = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.update, {
    onSuccess: data => invalidateAll(queryClient, periodId, data?.sheet_id),
    ...queryParams
  });
};

export const useDeleteSheetCategory = ({ periodId, sheetId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.deleteOne, {
    onSuccess: () => invalidateAll(queryClient, periodId, sheetId),
    ...queryParams
  });
};

// Copy the previous period's mapping onto this one, sheet by sheet, using the file pairing the
// period already has. Taken verbatim from `feat/mappings-copy`, which introduced it for the
// dashboard's "Add Mappings" shortcut — same endpoint, same payload, so the two collapse into one
// definition whenever the branches meet.
export const useCopySheetCategories = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.copyFromPreviousPeriod, {
    onSuccess: (_data, variables) => invalidateAll(queryClient, periodId || variables?.period_id),
    ...queryParams
  });
};

export const useDeleteAllSheetCategoriesForSheet = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(sheetCategoriesService.deleteAllForSheet, {
    onSuccess: (_data, sheetId) => invalidateAll(queryClient, periodId, sheetId),
    ...queryParams
  });
};

// The API clears one sheet per call, so a whole period is one call per sheet behind one mutation.
export const useClearSheetCategories = ({ periodId, mutationOptions = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    sheetIds => Promise.all(sheetIds.map(id => sheetCategoriesService.deleteAllForSheet(id))),
    {
      // Settled, not success: a run that failed part-way has still deleted some sheets' rows.
      onSettled: () => invalidateAll(queryClient, periodId),
      ...mutationOptions
    }
  );
};

// No ids: the LLM fills in the sheets that are still unmapped. With ids: it re-maps exactly those
// sheets, mapped or not. Response is `{ created, removed, skipped, considered_sheet_count }` —
// `created` / `removed` are SheetCategoryResponseSchema rows written and deleted, `skipped` carries
// the sheets it left alone and why (`unchanged` keeps the same categories, `no_match` keeps the old).
export const useAIMapSheetCategories = ({ periodId, queryParams = {} } = {}) => {
  const queryClient = useQueryClient();
  return useMutation(
    // The only mutation here that closes over the id rather than reading it off the mutate
    // variables, so it is the only one that can bake `undefined` into the URL. The queries say this
    // with `enabled`; a mutation has to say it itself.
    sheetIds =>
      periodId
        ? sheetCategoriesService.mapWithAI(periodId, sheetIds)
        : Promise.reject(new Error('periodId is required')),
    {
      onSuccess: () => invalidateAll(queryClient, periodId),
      ...queryParams
    }
  );
};
